import { z } from 'zod';

/**
 * Internal DSL for advanced automations. Compiles 1:1 to n8n nodes on save,
 * but the tenant only ever sees this constrained shape.
 *
 * Node kinds (v1.2 PRD):
 *   - trigger   · entry point (webhook / shopify / stripe / sheets / scheduled)
 *   - send      · send a template
 *   - delay     · wait N minutes/hours/days
 *   - split     · A/B with weights summing to 100
 *   - condition · if/else branch on payload path
 *   - end       · terminal node
 */

export const flowNodeKindSchema = z.enum(['trigger', 'send', 'delay', 'split', 'condition', 'end']);

export const flowNodePositionSchema = z.object({ x: z.number(), y: z.number() });

export const flowNodeTriggerSchema = z.object({
  id: z.string(),
  kind: z.literal('trigger'),
  position: flowNodePositionSchema,
  config: z.object({
    source: z.enum(['webhook', 'shopify', 'stripe', 'sheets', 'schedule']),
    event: z.string(),
  }),
});

export const flowNodeSendSchema = z.object({
  id: z.string(),
  kind: z.literal('send'),
  position: flowNodePositionSchema,
  config: z.object({
    templateId: z.string().uuid(),
    providerConnectionId: z.string().uuid().optional(),
  }),
});

export const flowNodeDelaySchema = z.object({
  id: z.string(),
  kind: z.literal('delay'),
  position: flowNodePositionSchema,
  config: z.object({
    amount: z.number().int().positive(),
    unit: z.enum(['minutes', 'hours', 'days']),
  }),
});

export const flowNodeSplitSchema = z.object({
  id: z.string(),
  kind: z.literal('split'),
  position: flowNodePositionSchema,
  config: z.object({
    branches: z
      .array(
        z.object({
          id: z.string(),
          label: z.string(),
          weight: z.number().int().min(0).max(100),
        }),
      )
      .min(2)
      .max(4),
  }),
});

export const flowNodeConditionSchema = z.object({
  id: z.string(),
  kind: z.literal('condition'),
  position: flowNodePositionSchema,
  config: z.object({
    path: z.string(),
    operator: z.enum(['equals', 'not_equals', 'contains', 'gt', 'lt', 'exists']),
    value: z.string().optional(),
  }),
});

export const flowNodeEndSchema = z.object({
  id: z.string(),
  kind: z.literal('end'),
  position: flowNodePositionSchema,
  config: z.object({}).default({}),
});

export const flowNodeSchema = z.discriminatedUnion('kind', [
  flowNodeTriggerSchema,
  flowNodeSendSchema,
  flowNodeDelaySchema,
  flowNodeSplitSchema,
  flowNodeConditionSchema,
  flowNodeEndSchema,
]);

export const flowEdgeSchema = z.object({
  id: z.string(),
  from: z.string(),
  to: z.string(),
  branch: z.string().optional(), // for split/condition branches
});

export const flowDocumentSchema = z.object({
  version: z.literal(1),
  nodes: z.array(flowNodeSchema),
  edges: z.array(flowEdgeSchema),
});

export type FlowNodeKind = z.infer<typeof flowNodeKindSchema>;
export type FlowNode = z.infer<typeof flowNodeSchema>;
export type FlowEdge = z.infer<typeof flowEdgeSchema>;
export type FlowDocument = z.infer<typeof flowDocumentSchema>;
